import {
  getRemoteConfig,
  fetchAndActivate,
  getValue,
  type RemoteConfig,
  type Value,
} from "firebase/remote-config";
import type { FirebaseApp } from "firebase/app";
import type { FeatureFlagName } from "./FeatureFlagManager";
import { initialState } from "./initialState";

export interface RemoteConfigAdapter {
  initialize(): Promise<void>;
  getRemoteValue(flag: FeatureFlagName): boolean | undefined;
  getAllRemoteValues(): Record<FeatureFlagName, boolean>;
}

export interface FirebaseRemoteConfigWrapper {
  getRemoteConfig(app: FirebaseApp): RemoteConfig;
  fetchAndActivate(remoteConfig: RemoteConfig): Promise<boolean>;
  getValue(remoteConfig: RemoteConfig, key: string): Value;
}

export class ProductionFirebaseRemoteConfigWrapper
  implements FirebaseRemoteConfigWrapper
{
  getRemoteConfig(app: FirebaseApp): RemoteConfig {
    return getRemoteConfig(app);
  }

  fetchAndActivate(remoteConfig: RemoteConfig): Promise<boolean> {
    return fetchAndActivate(remoteConfig);
  }

  getValue(remoteConfig: RemoteConfig, key: string): Value {
    return getValue(remoteConfig, key);
  }
}

export class FirebaseRemoteConfigAdapter implements RemoteConfigAdapter {
  private remoteConfig: RemoteConfig;
  private isInitialized = false;

  constructor(
    firebaseApp: FirebaseApp,
    private firebaseWrapper: FirebaseRemoteConfigWrapper = new ProductionFirebaseRemoteConfigWrapper(),
  ) {
    this.remoteConfig = this.firebaseWrapper.getRemoteConfig(firebaseApp);

    this.remoteConfig.settings = {
      minimumFetchIntervalMillis: 30000,
      fetchTimeoutMillis: 60000,
    };

    const defaults: Record<string, boolean> = {};
    Object.entries(initialState).forEach(([key, config]) => {
      defaults[key] = config.defaultValue;
    });
    this.remoteConfig.defaultConfig = defaults;
  }

  async initialize(): Promise<void> {
    if (this.isInitialized) {
      return;
    }

    try {
      await this.firebaseWrapper.fetchAndActivate(this.remoteConfig);
      this.isInitialized = true;
      console.info("Firebase Remote Config initialized successfully");
    } catch (error) {
      console.warn(
        "Failed to initialize Firebase Remote Config, falling back to defaults:",
        error,
      );

      this.isInitialized = true;
    }
  }

  getRemoteValue(flag: FeatureFlagName): boolean | undefined {
    if (!this.isInitialized) {
      return undefined;
    }

    try {
      const value = this.firebaseWrapper.getValue(this.remoteConfig, flag);
      return value.asBoolean();
    } catch (error) {
      console.warn(
        `Failed to get remote config value for flag ${flag}:`,
        error,
      );
      return undefined;
    }
  }

  getAllRemoteValues(): Record<FeatureFlagName, boolean> {
    const remoteValues = {} as Record<FeatureFlagName, boolean>;

    Object.keys(initialState).forEach((flag) => {
      const remoteValue = this.getRemoteValue(flag as FeatureFlagName);
      if (remoteValue !== undefined) {
        remoteValues[flag as FeatureFlagName] = remoteValue;
      }
    });

    return remoteValues;
  }
}
